/**
 * WelcomeDialog — Popup de bienvenue AITO.PF (V12.x)
 *
 * - Affichée automatiquement à la première visite (localStorage).
 * - Fermeture normale = one-shot par session (sessionStorage) → ne revient pas
 *   à chaque changement de page ni à chaque navigation.
 * - Case « Ne plus afficher » = mémorisation permanente (localStorage).
 * - Non intrusive : Escape ferme, focus initial sur le CTA principal,
 *   ne re-apparaît pas si l'utilisateur navigue entre les pages.
 * - Ne s'affiche PAS pour les utilisateurs déjà connectés (ils connaissent
 *   déjà AITO.PF ; on évite le bruit).
 *
 * Utilise le Dialog shadcn (accessibilité clavier / focus trap fournis).
 */
import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Sparkles, UserPlus, ArrowRight } from "lucide-react";
import { useAuth } from "@/lib/auth";

const LS_KEY = "aito.welcomePopup.dismissed.v1";
const SS_KEY = "aito.welcomePopup.shown.v1";

function shouldAutoShow() {
  try {
    if (typeof window === "undefined") return false;
    // Choix permanent : ne plus afficher
    if (window.localStorage.getItem(LS_KEY) === "1") return false;
    // Déjà affichée dans cette session (fermeture standard)
    if (window.sessionStorage.getItem(SS_KEY) === "1") return false;
    return true;
  } catch {
    return false;
  }
}

export default function WelcomeDialog() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [neverAgain, setNeverAgain] = useState(false);
  const primaryBtnRef = useRef(null);

  useEffect(() => {
    // Pour les utilisateurs connectés, on ne montre pas la popup d'accueil.
    if (user) return;
    if (shouldAutoShow()) {
      // Micro-délai pour laisser le hero se peindre → UX plus douce
      const t = setTimeout(() => setOpen(true), 350);
      return () => clearTimeout(t);
    }
  }, [user]);

  const persistDismiss = () => {
    try {
      // Toujours marquer la session comme vue → pas de re-flash après fermeture.
      window.sessionStorage.setItem(SS_KEY, "1");
      if (neverAgain) window.localStorage.setItem(LS_KEY, "1");
    } catch {
      /* ignore quotas / private mode */
    }
  };

  const handleOpenChange = (v) => {
    setOpen(v);
    if (!v) persistDismiss();
  };

  const closeDialog = () => handleOpenChange(false);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-lg p-0 gap-0 overflow-hidden"
        data-testid="welcome-dialog"
        onOpenAutoFocus={(e) => {
          // Focus explicite sur le CTA principal (meilleure a11y).
          e.preventDefault();
          primaryBtnRef.current?.focus();
        }}
      >
        {/* Bandeau visuel — arrière-plan officiel du site (hero-banniere) + overlay dégradé AITO lagon/or, logo centré */}
        <div
          className="relative px-6 py-7 sm:py-8 text-white overflow-hidden"
          style={{
            backgroundImage:
              'linear-gradient(135deg, hsl(var(--aito-lagoon)) 0%, hsl(var(--aito-lagoon)) 35%, rgba(214,167,58,0.72) 100%), url("/branding/hero-banniere.jpg")',
            backgroundBlendMode: "multiply, normal",
            backgroundSize: "cover, cover",
            backgroundPosition: "center, center",
          }}
        >
          <div className="absolute inset-0 bg-black/15 pointer-events-none" />
          <div className="relative flex flex-col items-center text-center">
            <img
              src="/branding/aito-logo.png"
              alt="AITO.PF"
              className="h-14 w-auto sm:h-16 mb-3 drop-shadow-lg"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
            <DialogTitle className="font-display text-2xl sm:text-3xl font-bold tracking-tight leading-tight drop-shadow-md">
              Bienvenue sur AITO.PF
            </DialogTitle>
            <DialogDescription className="mt-1.5 text-sm sm:text-base text-white/95 drop-shadow">
              La plateforme du sport polynésien, pensée pour les sportifs.
            </DialogDescription>
          </div>
        </div>

        {/* Corps du message */}
        <div className="px-6 py-5 sm:py-6 space-y-4">
          <p className="text-sm sm:text-[15px] leading-relaxed text-foreground">
            Crée ta fiche, valorise ton parcours, centralise tes résultats,
            ton palmarès et tes articles, et retrouve les athlètes, coachs,
            clubs et fédérations du sport polynésien.
          </p>
          <p className="text-sm sm:text-[15px] leading-relaxed text-muted-foreground">
            AITO.PF a aussi une ambition forte&nbsp;: préserver la mémoire
            des résultats, des parcours et de l'histoire du sport en Polynésie,
            pour mieux les valoriser aujourd'hui et les transmettre demain.
          </p>

          {/* Phrase manifeste — mise en valeur */}
          <div className="rounded-lg border-l-4 border-[hsl(var(--aito-gold))] bg-[hsl(var(--aito-gold))]/10 px-4 py-3">
            <p className="font-display font-semibold text-sm sm:text-[15px] leading-snug text-foreground">
              Ton parcours mérite d'être visible.
              <br />
              Ton histoire mérite d'être conservée.
            </p>
          </div>

          {/* CTAs */}
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-1">
            <Button
              variant="outline"
              onClick={closeDialog}
              asChild
              data-testid="welcome-cta-signup"
              className="sm:order-2"
            >
              <Link to="/connexion?tab=register">
                <UserPlus className="h-4 w-4 mr-1.5" />
                Créer mon compte
              </Link>
            </Button>
            <Button
              ref={primaryBtnRef}
              onClick={closeDialog}
              data-testid="welcome-cta-discover"
              className="bg-[hsl(var(--aito-lagoon))] hover:bg-[hsl(var(--aito-lagoon))]/90 text-white sm:order-3"
            >
              <Sparkles className="h-4 w-4 mr-1.5" />
              Découvrir AITO.PF
              <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
          </div>

          {/* "Ne plus afficher" */}
          <label
            htmlFor="welcome-never-again"
            className="flex items-center gap-2 pt-2 text-xs text-muted-foreground cursor-pointer select-none"
          >
            <Checkbox
              id="welcome-never-again"
              checked={neverAgain}
              onCheckedChange={(v) => setNeverAgain(v === true)}
              data-testid="welcome-never-again"
            />
            Ne plus afficher ce message
          </label>
        </div>
      </DialogContent>
    </Dialog>
  );
}
